import {AppDispatch} from '../store';
import {ProductionStageType} from '../types/enums/ProductionStageType';
import {ReportSection} from '../types/enums/ReportSections';
import {IncidentType} from '../types/IncidentType';
import {formatLocalDate} from '../util/DateUtil';
import {deleteJsonData, getJsonData, postFormData, postJsonData} from './requests';

/**
 * Incident data prepared for API transmission where Date fields are converted to strings.
 * This type represents the incident as it will be serialized to JSON.
 */
type IncidentApiPayload = Omit<IncidentType, 'dueDate'> & {
    dueDate: string | Date;
};

/**
 * Prepares an incident object for API transmission by converting Date objects
 * to local date strings (YYYY-MM-DD format).
 *
 * This prevents timezone issues where JSON.stringify() converts Date objects
 * to UTC ISO strings, causing dates to shift by one day in CET/CEST timezone.
 *
 * @param incident - The incident object to prepare
 * @returns A new incident object with Date fields converted to local date strings
 */
const prepareIncidentForApi = (incident: IncidentType): IncidentApiPayload => {
    return {
        ...incident,
        dueDate: incident.dueDate instanceof Date
            ? formatLocalDate(incident.dueDate)
            : incident.dueDate
    };
};

export const getIncidentById = (dispatch: AppDispatch, id: number) => {
    const url = `api/1.0/incidents/${id}`;
    return getJsonData(url, dispatch);
};

export const deleteIncident = (dispatch: AppDispatch, incidentId: number) => {
    const url = `api/1.0/incidents/${incidentId}`;
    return deleteJsonData(url, dispatch);
};
export const updateIncidentStatus = (id: number, workflowStage: string, dispatch: AppDispatch) => {
    const url = 'api/1.0/incidents/update-status';
    const dto = {id: id, workflowStage: workflowStage};
    return postJsonData(url, JSON.stringify(dto), dispatch);
};

export const getIncidentsCategoriesBySectionType = (dispatch: AppDispatch, reportSectionType: ReportSection) => {
    const url = `api/1.0/incidents/category/${reportSectionType}`;
    return getJsonData(url, dispatch);
};

export const uploadIncidentDocuments = (dispatch: AppDispatch, incidentId: number, files: File[]) => {
    const formData = new FormData();

    const documents = files.map(file => ({
        file: file,
        name: file.name,
        fileId: file.name + '$$' + file.size
    }));

    formData.append('META', new Blob([JSON.stringify(documents)], {type: 'application/json'}));

    for (const file of files) {
        formData.append('FILES', file);
    }

    const url = `api/1.0/incidents/files/${incidentId}`;
    return postFormData(url, formData, dispatch);
};

export const deleteIncidentDocument = (dispatch: AppDispatch, incidentId: number, documentId: number) => {
    const url = `api/1.0/incidents/file/${incidentId}/${documentId}`;
    return deleteJsonData(url, dispatch);
};

export const createOrUpdateIncidentPartially = (dispatch: AppDispatch, reportSectionId: number | undefined, incident: IncidentType, updatedFields: string[]) => {
    const url = `api/1.0/incidents/partial${reportSectionId ? ('/' + reportSectionId) : ''}`;
    const preparedIncident = prepareIncidentForApi(incident);
    return postJsonData(url, JSON.stringify({incident: preparedIncident, updatedFields}), dispatch);
};

export const countUnreadIncidents = (dispatch: AppDispatch) => {
    const url = 'api/1.0/incidents/unread';
    return getJsonData(url, dispatch);
};

export const markIncidentRead = (dispatch: AppDispatch, incidentId: number) => {
    const url = `api/1.0/incidents/mark-read/${incidentId}`;
    return getJsonData(url, dispatch);
};

export const getIncidentsByProductionIds = (dispatch: AppDispatch, productionIds: string[] | number[]) => {
    const query = new URLSearchParams();
    productionIds.forEach(value => query.append('productionId', `${value}`));
    const url = `api/1.0/incidents/production?`;
    return getJsonData(url + query.toString(), dispatch);
};

export const getSevereIncidentsByProductionIdAndStage = (dispatch: AppDispatch, productionId: string, productionStage: ProductionStageType | undefined) => {
    let url = `api/1.0/incidents/severe?productionId=${productionId}`;
    url += productionStage ? `&productionStage=${productionStage}` : '';
    return getJsonData(url, dispatch);
};
